import { useState } from "react";
import { Outlet, Link, NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  Bell,
  User,
  LogOut,
  ChevronDown,
  Menu,
  X,
  Home,
  Receipt,
  FolderOpen,
  UserCircle,
  Zap,
  Eye,
} from "lucide-react";
import {
  cn,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  ThemeToggle,
} from "@lsf/ui";
import { useAuth } from "@/lib/auth/AuthProvider";
import { useTenantDashboardQuery } from "@/lib/api/queries";

const navigation = [
  { name: "Dashboard", href: "/", icon: LayoutDashboard, exact: true },
  { name: "My Invoices", href: "/invoices", icon: FileText },
  { name: "Notices", href: "/notices", icon: Bell },
  { name: "Transactions", href: "/transactions", icon: Receipt },
  { name: "Electricity", href: "/electricity", icon: Zap },
  { name: "Documents", href: "/documents", icon: FolderOpen },
  { name: "Profile", href: "/profile", icon: User },
];

export function TenantLayout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { user, logout, impersonation, stopImpersonating } = useAuth();

  // Dashboard query feeds the badge count on the bottom-nav notices icon.
  // Cached, so opening the dashboard once means the badge is always fresh.
  // Unread (not just pending-mandatory) so optional notices show up too —
  // this is the whole "so they don't claim to have not seen it" signal.
  const { data: dashboard } = useTenantDashboardQuery();
  const unreadCount = dashboard?.unread_notice_count ?? 0;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sticky group: banner (when present) + header stick together as one unit */}
      <div className="sticky top-0 z-50">
        {impersonation?.active && (
          <div className="bg-amber-500 text-white px-4 py-2 flex items-center justify-center gap-3 text-sm font-medium flex-wrap">
            <Eye className="w-4 h-4 shrink-0" />
            <span>
              Estate management is viewing this account
              {impersonation.tenant_name ? ` (${impersonation.tenant_name})` : ""}, read-only.
            </span>
            <button
              type="button"
              onClick={() => stopImpersonating()}
              className="underline font-semibold hover:no-underline"
            >
              Return to Admin
            </button>
          </div>
        )}
        <header className="bg-white/80 backdrop-blur-lg border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <Link to="/" className="flex items-center gap-2 shrink-0">
              <div className="w-9 h-9 bg-white rounded-xl flex items-center justify-center shadow-md border border-slate-200 shrink-0">
                <img src="/logo.png" alt="LSF Estate" className="w-6 h-6 object-contain" />
              </div>
              <div className="flex flex-col justify-center leading-tight whitespace-nowrap">
                <div className="font-bold text-sm sm:text-base bg-linear-to-r from-primary to-accent bg-clip-text text-transparent">
                  LSF Estate
                </div>
                <div className="text-xs text-muted-foreground hidden sm:block font-medium">
                  Tenant Portal
                </div>
              </div>
            </Link>

            <nav className="hidden md:flex items-center gap-1">
              {navigation.map((item) => {
                const isActive = item.exact
                  ? location.pathname === item.href
                  : location.pathname === item.href ||
                    location.pathname.startsWith(item.href + "/");
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={cn(
                      "flex items-center gap-2 px-4 py-2 rounded-xl transition-all text-sm font-medium",
                      isActive
                        ? "bg-linear-to-r from-accent to-[#316bf3] text-white shadow-md"
                        : "text-muted-foreground hover:bg-slate-100 hover:text-foreground",
                    )}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{item.name}</span>
                    {item.href === "/notices" && unreadCount > 0 && (
                      <span className="ml-1 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-destructive text-white text-[10px] font-semibold">
                        {unreadCount}
                      </span>
                    )}
                  </Link>
                );
              })}
            </nav>

            <div className="flex items-center gap-1 sm:gap-2">
              <ThemeToggle />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2 h-10 sm:h-9 hover:bg-slate-100">
                    <div className="w-8 h-8 rounded-full bg-linear-to-br from-accent to-[#316bf3] flex items-center justify-center shadow-md ring-2 ring-white">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <span className="hidden sm:inline text-sm font-medium">
                      {user?.full_name?.split(" ")[0] ?? "Tenant"}
                    </span>
                    <ChevronDown className="w-4 h-4 hidden sm:block text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="font-medium">{user?.full_name}</div>
                    <div className="text-xs text-muted-foreground">{user?.username ?? user?.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/profile">Profile</Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onSelect={() => logout()}
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <button
                type="button"
                onClick={() => setMobileMenuOpen((v) => !v)}
                className="md:hidden text-foreground p-2 -mr-2 touch-manipulation hover:bg-slate-100 active:bg-slate-200 rounded-xl transition-colors"
                aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white/95 backdrop-blur-lg">
            <nav className="px-3 py-3 space-y-1">
              {navigation.map((item) => {
                const isActive = item.exact
                  ? location.pathname === item.href
                  : location.pathname === item.href ||
                    location.pathname.startsWith(item.href + "/");
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={cn(
                      "flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all touch-manipulation",
                      isActive
                        ? "bg-linear-to-r from-accent to-[#316bf3] text-white shadow-md"
                        : "text-muted-foreground active:bg-slate-100 hover:bg-slate-100 hover:text-foreground",
                    )}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-semibold">{item.name}</span>
                  </Link>
                );
              })}
            </nav>
          </div>
        )}
      </header>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 min-h-[calc(100vh-3.5rem)] md:min-h-[calc(100vh-4rem)] pb-24 md:pb-6">
        <Outlet />
      </main>

      {/* Mobile bottom nav — sticky 4-tab dock */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-lg border-t border-slate-200 shadow-2xl safe-area-pb">
        <div className="grid grid-cols-4 h-16">
          <BottomTab to="/" icon={Home} label="Home" exact />
          <BottomTab to="/invoices" icon={Receipt} label="Invoices" />
          <BottomTab to="/notices" icon={Bell} label="Notices" badge={unreadCount} />
          <BottomTab to="/profile" icon={UserCircle} label="Profile" />
        </div>
      </nav>

      {/* Floating notification button */}
      <Link
        to="/notices"
        className="fixed right-5 bottom-24 md:bottom-6 z-50 w-14 h-14 rounded-full flex items-center justify-center shadow-2xl active:scale-95 transition-transform touch-manipulation"
        style={{ background: "#0051d5" }}
        aria-label="Notices"
      >
        <Bell className="w-6 h-6 text-white" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white border-2 border-white"
            style={{ background: "#ba1a1a" }}
          >
            {unreadCount}
          </span>
        )}
      </Link>
    </div>
  );
}

function BottomTab({
  to,
  icon: Icon,
  label,
  badge,
  exact,
}: {
  to: string;
  icon: typeof Home;
  label: string;
  badge?: number;
  exact?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={exact}
      className={({ isActive }) =>
        cn(
          "relative flex flex-col items-center justify-center gap-1 transition-all touch-manipulation",
          isActive ? "text-accent" : "text-muted-foreground active:text-accent",
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-12 h-1 bg-linear-to-r from-accent to-[#316bf3] rounded-b-full" />
          )}
          <Icon className={cn("w-6 h-6 transition-transform", isActive && "scale-110")} />
          <span className="text-xs font-medium">{label}</span>
          {badge !== undefined && badge > 0 && (
            <span className="absolute top-1.5 right-5 w-5 h-5 bg-destructive text-white rounded-full text-xs flex items-center justify-center font-semibold shadow-lg">
              {badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}
